'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
export interface SectionConfig<T extends string> {
  id: T;
  label: string;
  requiresConnectedWallet?: boolean;
}
interface UseSectionScrollSpyOptions<T extends string> {
  sections: SectionConfig<T>[];
  defaultSection: T;
  stickyOffset: number | (() => number);
}
interface UseSectionScrollSpyReturn<T extends string> {
  activeSection: T;
  scrollToSection: (sectionId: T) => void;
  registerSection: (sectionId: T, element: HTMLElement | null) => void;
}
const ACTIVE_LINE_TOP_INSET = 48;
const PROGRAMMATIC_SCROLL_TIMEOUT_MS = 800;
const PAGE_BOTTOM_THRESHOLD_PX = 2;

// True when the page is scrolled to (or within a px of) its bottom. Used so a
// final section too short to push its top above the reference line still wins
// once there's no more to scroll.
function isAtPageBottom() {
  const {
    scrollHeight
  } = document.documentElement;
  return scrollHeight > window.innerHeight && window.scrollY + window.innerHeight >= scrollHeight - PAGE_BOTTOM_THRESHOLD_PX;
}
export function useSectionScrollSpy<T extends string>({
  sections,
  defaultSection,
  stickyOffset
}: UseSectionScrollSpyOptions<T>): UseSectionScrollSpyReturn<T> {
  // Starts on `defaultSection` on both server and client so the hydrated tab
  // markup matches the server HTML; the URL hash / `?subTab=` deep link is
  // applied in the mount-only effect below.
  const [activeSection, setActiveSection] = useState<T>(defaultSection);
  const getStickyOffset = useCallback(() => typeof stickyOffset === 'function' ? stickyOffset() : stickyOffset, [stickyOffset]);
  const sectionElements = useRef<Map<T, HTMLElement>>(new Map());
  const isScrollingProgrammatically = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const scrollRafRef = useRef<number>(undefined);
  useEffect(() => {
    return () => {
      clearTimeout(scrollTimeoutRef.current);
      cancelAnimationFrame(scrollRafRef.current!);
    };
  }, []);
  const registerSection = (sectionId: T, element: HTMLElement | null) => {
    if (element) {
      sectionElements.current.set(sectionId, element);
    } else {
      sectionElements.current.delete(sectionId);
    }
  };

  // Latest sections, read inside the scroll callback so the effect can be keyed
  // on a stable signal (`sectionKey`) instead of the array identity (which
  // changes every render and would re-attach the listeners each time).
  const sectionsRef = useRef(sections);
  useEffect(() => {
    sectionsRef.current = sections;
  });
  const sectionKey = sections.map(s => s.id).join(',');
  useEffect(() => {
    if (sectionsRef.current.length === 0) return;

    // The active section is the lowest one in DOM order whose top edge has
    // scrolled above the reference line just under the sticky tab bar.
    //
    // Recompute from the full set of registered sections on every scroll frame.
    // (An earlier IntersectionObserver implementation only fired on band
    // enter/exit — not when a heading crossed the reference line — so the
    // active section lagged and could stick on the wrong one: VAU-1107.)
    const computeActiveSection = (): T | undefined => {
      const reference = getStickyOffset() + ACTIVE_LINE_TOP_INSET;
      let current: T | undefined;
      let lastVisible: T | undefined;
      for (const {
        id
      } of sectionsRef.current) {
        const el = sectionElements.current.get(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        // Skip sections with no layout box — e.g. a mobile tab panel kept
        // mounted via `forceMount` but hidden with `display:none`. A zero rect
        // would otherwise read as "above the line", wrongly select the last
        // hidden section, and rewrite the hash while it isn't visible. When
        // every section is hidden, `current` stays undefined and `sync` bails.
        if (rect.width === 0 && rect.height === 0) continue;
        current ??= id;
        lastVisible = id;
        if (rect.top - reference <= 0) current = id;
      }
      // A final section shorter than the viewport can never push its top above
      // the reference line; once scrolled to the bottom it's still the section
      // in view, so treat scroll-at-bottom as the last visible section.
      if (lastVisible !== undefined && isAtPageBottom()) return lastVisible;
      return current;
    };
    const sync = () => {
      rafId = undefined;
      if (isScrollingProgrammatically.current) return;
      const current = computeActiveSection();
      if (!current) return;
      setActiveSection(current);
      // A fragment in the URL makes the browser scroll to it when a streamed page
      // finishes parsing, and on every refresh or shared link. Leave the URL
      // alone until the page has parsed and the user has moved off the default.
      if (document.readyState === 'loading') return;
      if (current === defaultSection && !window.location.hash) return;
      if (window.location.hash !== `#${current}`) {
        try {
          history.replaceState(null, '', `#${current}`);
        } catch {
          // Throws in cross-origin iframes (SafeApp)
        }
      }
    };

    // Coalesce bursts of scroll/resize events into one measurement per frame.
    let rafId: number | undefined;
    const schedule = () => {
      if (rafId === undefined) rafId = requestAnimationFrame(sync);
    };
    window.addEventListener('scroll', schedule, {
      passive: true
    });
    window.addEventListener('resize', schedule, {
      passive: true
    });

    // Any layout change can shift the sections' offsets without firing
    // scroll/resize: a section's own content settling in (lazy-loaded
    // charts/tables) OR content above the tabs (e.g. a hero Suspense fallback
    // resolving). Observe `document.body`, whose height tracks the page
    // content, so both are covered — and it never goes stale as sections
    // remount.
    const layoutObserver = new ResizeObserver(schedule);
    layoutObserver.observe(document.body);

    // Measure the current layout immediately so a restored scroll position
    // (browser back/forward, refresh mid-page) reflects the right section on
    // mount instead of waiting for the first scroll/resize.
    schedule();
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      layoutObserver.disconnect();
      if (rafId !== undefined) cancelAnimationFrame(rafId);
    };
  }, [sectionKey, getStickyOffset, defaultSection]);
  const scrollToSection = (sectionId: T) => {
    const el = sectionElements.current.get(sectionId);
    if (!el) return;
    isScrollingProgrammatically.current = true;
    setActiveSection(sectionId);
    try {
      history.replaceState(null, '', `#${sectionId}`);
    } catch {
      // Throws in cross-origin iframes (SafeApp)
    }

    // Deferred so it runs after TabGroup's own scrollIntoView on the tab button
    cancelAnimationFrame(scrollRafRef.current!);
    scrollRafRef.current = requestAnimationFrame(() => {
      el.scrollIntoView({
        behavior: 'smooth'
      });
    });
    clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingProgrammatically.current = false;
    }, PROGRAMMATIC_SCROLL_TIMEOUT_MS);
  };

  // Mount-only: initial hash/subTab navigation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // oxlint-disable-next-line typescript/consistent-type-assertions
    const subTab = params.get('subTab') as T | null;
    // oxlint-disable-next-line typescript/consistent-type-assertions
    const hash = window.location.hash.slice(1) as T;
    const isSection = (id: T | null): id is T => !!id && sections.some(s => s.id === id);
    const target = isSection(hash) ? hash : isSection(subTab) ? subTab : null;
    if (target && target !== defaultSection) {
      setActiveSection(target);
      requestAnimationFrame(() => scrollToSection(target));
    }
    if (subTab) {
      params.delete('subTab');
      const search = params.toString();
      const newUrl = search ? `${window.location.pathname}?${search}${window.location.hash}` : `${window.location.pathname}${window.location.hash}`;
      try {
        history.replaceState(null, '', newUrl);
      } catch {
        // Throws in cross-origin iframes (SafeApp)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only by design
  }, []);
  return {
    activeSection,
    scrollToSection,
    registerSection
  };
}